import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Truck, Save, Loader2, Zap, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function YurticiSettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [config, setConfig] = useState({
    username: "",
    password: "",
    language: "TR",
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await api.get("/yurtici/config");
        if (!mounted) return;
        if (data.configured) {
          setConfigured(true);
          setConfig((prev) => ({
            ...prev,
            username: data.username || "",
            language: data.language || "TR",
          }));
        }
      } catch (error) {
        console.error("Yurtici config fetch error:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleSave = async () => {
    if (!config.username || !config.password) {
      toast.error("Kullanıcı adı ve şifre zorunludur.");
      return;
    }
    setSaving(true);
    try {
      const result = await api.post("/yurtici/save-config", config);
      if (result.success) {
        toast.success("Yurtiçi Kargo ayarları kaydedildi");
        setConfigured(true);
      } else {
        throw new Error(result.message || "Kaydetme hatası");
      }
    } catch (err) {
      toast.error(err.message || "Yurtiçi ayarları kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await api.post("/yurtici/test");
      if (result.success) {
        toast.success(result.message || "Bağlantı başarılı!");
      } else {
        toast.error(result.message || "Bağlantı hatası");
      }
    } catch (err) {
      toast.error(err.message || "Bağlantı test edilemedi");
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    setSaving(true);
    try {
      const result = await api.post("/yurtici/disconnect");
      if (result.success) {
        toast.success("Yurtiçi Kargo bağlantısı kaldırıldı");
        setConfigured(false);
        setConfig({ username: "", password: "", language: "TR" });
      } else {
        toast.error(result.message || "Bağlantı kaldırılamadı");
      }
    } catch (err) {
      toast.error(err.message || "Bağlantı kaldırılamadı");
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const closePanel = () => navigate("/panel/entegrasyonlar");

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <div className="fixed inset-0 z-40 bg-slate-950/40" onClick={closePanel} />
      <div className="fixed right-0 top-0 z-[400] h-screen w-full max-w-full sm:max-w-[680px] overflow-y-auto bg-card shadow-2xl">
        <div className="border-b border-border bg-card px-4 py-4 sm:px-5">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={closePanel} className="h-8 w-8">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="w-9 h-9 rounded-lg border bg-card flex items-center justify-center overflow-hidden">
              <Truck className="h-5 w-5 text-blue-600" />
            </div>
            <div className="text-left">
              <div className="text-lg font-semibold leading-tight">Yurtiçi Kargo Entegrasyonu</div>
              <div className="text-xs text-muted-foreground">Kargo API bağlantı ayarları</div>
            </div>
            {configured ? (
              <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Bağlı
              </span>
            ) : null}
          </div>
        </div>

        <div className="px-4 sm:px-5 py-4 space-y-4">
          <div className="surface-card p-5 space-y-4">
            <div>
              <div className="text-sm font-semibold">API Kimlik Bilgileri</div>
              <div className="text-xs text-muted-foreground mt-0.5">Yurtiçi Kargo kullanıcı adı ve şifrenizi girin. Kaydettikten sonra sistem bu bilgileri kullanarak gönderi oluşturabilir.</div>
            </div>
              {configured ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-sm text-emerald-800">Yurtiçi Kargo bağlantısı aktif.</div>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <Label>Kullanıcı Adı</Label>
                    <Input value={config.username} onChange={(e) => updateConfig("username", e.target.value)} placeholder="Kullanıcı adınızı girin" data-testid="yurtici-username" />
                  </div>
                  <div>
                    <Label>Şifre</Label>
                    <div className="relative">
                      <Input type={showPassword ? "text" : "password"} value={config.password} onChange={(e) => updateConfig("password", e.target.value)} placeholder="********" className="pr-10" data-testid="yurtici-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </>
              )}
          </div>

          <div className="surface-card p-5 space-y-4">
            <div>
              <div className="text-sm font-semibold">Dil</div>
              <div className="text-xs text-muted-foreground mt-0.5">Yurtiçi Kargo API isteği için kullanılacak dil.</div>
            </div>
            <div>
              <Label>Dil</Label>
              <Input value={config.language} onChange={(e) => updateConfig("language", e.target.value)} placeholder="TR" data-testid="yurtici-language" />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-border bg-card px-4 sm:px-5 py-3 flex flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="yurtici-test-btn">
            {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
            Bağlantıyı Test Et
          </Button>
          {!configured ? (
            <Button onClick={handleSave} disabled={saving} data-testid="yurtici-save-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Kaydet
            </Button>
          ) : (
            <Button variant="destructive" onClick={handleDisconnect} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Bağlantıyı Kaldır
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
